"use client";

import { RefObject, useEffect, useRef } from "react";
import { NAV_ACTION } from "@/utils/constants";

interface NavActionHandlers {
  onSearch: () => void;
  onCart: () => void;
  onAccount?: () => void;
  onMobileMenu?: () => void;
  onMobileClose?: () => void;
  onDayPart?: (id: string) => void;
}

export function useNavActionBindings(
  containerRef: RefObject<HTMLElement | null>,
  handlers: NavActionHandlers,
  deps: readonly unknown[]
) {
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const listener = (event: Event) => {
      const target = event.target as Element | null;
      const actionElement =
        (target && typeof target.closest === "function"
          ? target.closest<HTMLElement>("[data-wse-nav-action]")
          : null);
      if (!actionElement) return;

      const action = actionElement.dataset.wseNavAction;
      let handler: (() => void) | undefined;
      switch (action) {
        case NAV_ACTION.SEARCH:
          handler = handlersRef.current.onSearch;
          break;
        case NAV_ACTION.CART:
          handler = handlersRef.current.onCart;
          break;
        case NAV_ACTION.ACCOUNT:
          handler = handlersRef.current.onAccount;
          break;
        case NAV_ACTION.MOBILE_MENU:
          handler = handlersRef.current.onMobileMenu;
          break;
        case NAV_ACTION.MOBILE_CLOSE:
          handler = handlersRef.current.onMobileClose;
          break;
        case NAV_ACTION.DAYPART: {
          const dayPartId = actionElement.dataset.wseNavDaypartId;
          if (!dayPartId) return;
          handler = () => handlersRef.current.onDayPart?.(dayPartId);
          break;
        }
      }
      if (!handler) return;

      event.preventDefault();
      handler();
    };

    container.addEventListener("click", listener);

    return () => {
      container.removeEventListener("click", listener);
    };
    // The stable container delegates to whichever fragment DOM is current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, ...deps]);
}
